import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Field,
  FieldLabel,
  Input,
  Textarea,
} from 'prosci-ui';
import { UploadIcon as Upload } from '../../utils/icons';
import { useHubAppCopy } from '../../hooks';

interface CertificationFailureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitHelpRequest: (data: {
    yearCertified: string;
    companyAtTimeOfCertification: string;
    nameOfInstructor: string;
    alternateNames: string;
    uploadedFiles?: File[];
    otherNotes: string;
  }) => void;
  firstName: string;
  lastName: string;
  alternateEmail?: string;
  loading?: boolean;
}

export const CertificationFailureModal: React.FC<CertificationFailureModalProps> = ({
  isOpen,
  onClose,
  onSubmitHelpRequest,
  loading = false
}) => {
  const hubAppCopy = useHubAppCopy();
  const copy = hubAppCopy('portalCopy')?.subscriptionCopy?.certification?.failure;
  const [yearCertified, setYearCertified] = useState('');
  const [companyAtTimeOfCertification, setCompanyAtTimeOfCertification] = useState('');
  const [nameOfInstructor, setNameOfInstructor] = useState('');
  const [alternateNames, setAlternateNames] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [otherNotes, setOtherNotes] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmitHelpRequest({
      yearCertified,
      companyAtTimeOfCertification,
      nameOfInstructor,
      alternateNames,
      uploadedFiles,
      otherNotes
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setUploadedFiles(Array.from(e.target.files));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{copy?.title || 'More Information Needed'}</DialogTitle>
          <DialogDescription>
            {copy?.description || "We weren't able to verify your eligibility with that email address either for this tier. To complete the process, please provide the additional information requested below and our support team with reach back out to you."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Field className="space-y-2">
              <FieldLabel htmlFor="yearCertified">
                {copy?.labels?.yearCertified || 'Year Certified'}
              </FieldLabel>
              <Input
                id="yearCertified"
                type="text"
                value={yearCertified}
                onChange={(e) => setYearCertified(e.target.value)}
                disabled={loading}
              />
            </Field>

            <Field className="space-y-2">
              <FieldLabel htmlFor="companyAtTimeOfCertification">
                {copy?.labels?.company || 'Company at time of certification'}
              </FieldLabel>
              <Input
                id="companyAtTimeOfCertification"
                type="text"
                value={companyAtTimeOfCertification}
                onChange={(e) => setCompanyAtTimeOfCertification(e.target.value)}
                disabled={loading}
              />
            </Field>
          </div>

          <Field className="space-y-2">
            <FieldLabel htmlFor="nameOfInstructor">
              {copy?.labels?.instructor || 'Name of Instructor'}
            </FieldLabel>
            <Input
              id="nameOfInstructor"
              type="text"
              value={nameOfInstructor}
              onChange={(e) => setNameOfInstructor(e.target.value)}
              disabled={loading}
            />
          </Field>

          <Field className="space-y-2">
            <FieldLabel htmlFor="alternateNames">
              {copy?.labels?.alternateNames || "Alternate Name(s) that maybe have been used"}
            </FieldLabel>
            <p className="text-sm text-muted-foreground">
              {copy?.labels?.alternateNamesHint || '(i.e. maiden name, nicknames, etc)'}
            </p>
            <Input
              id="alternateNames"
              type="text"
              value={alternateNames}
              onChange={(e) => setAlternateNames(e.target.value)}
              disabled={loading}
            />
          </Field>

          <Field className="space-y-2">
            <FieldLabel htmlFor="fileUpload">
              {copy?.labels?.fileUpload || 'Upload Any Relevant Documents'}
            </FieldLabel>
            <div className="relative">
              <input
                id="fileUpload"
                type="file"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onChange={handleFileChange}
                disabled={loading}
                multiple
              />
              <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-input bg-background p-6 text-center hover:bg-accent/50 transition-colors">
                <Upload className="h-6 w-6 text-muted-foreground" />
                <span className="text-sm font-medium">{copy?.labels?.fileUploadText || 'File Upload'}</span>
                <span className="text-xs text-muted-foreground">{copy?.labels?.fileUploadSubtext || 'Drag and Drop or click to Browse Files'}</span>
              </div>
            </div>
            {uploadedFiles.length > 0 && (
              <div className="mt-2 space-y-1">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="text-sm text-muted-foreground">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </Field>

          <Field className="space-y-2">
            <FieldLabel htmlFor="otherNotes">
              {copy?.labels?.otherNotes || "Any other notes you'd like to provide?"}
            </FieldLabel>
            <Textarea
              id="otherNotes"
              value={otherNotes}
              onChange={(e) => setOtherNotes(e.target.value)}
              disabled={loading}
              rows={4}
            />
          </Field>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={loading}
            >
              {copy?.buttons?.cancel || 'Cancel'}
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading}
            >
              {loading ? (copy?.buttons?.submitting || 'Submitting...') : (copy?.buttons?.submit || 'Submit Information')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CertificationFailureModal;
